import { create } from 'zustand';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';
import api from '../api';
import { getCurrentProductId } from '../product';

interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  is_authorized?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  isLoading: boolean;
  error: string | null;
  initialize: () => () => void; // Returns unsubscribe function
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isAuthorized: false,
  isLoading: true,
  error: null,

  initialize: () => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          const response = await api.post('/auth/login', { idToken });
          const user = response.data.user;
          // Check both is_authorized (snake_case from backend) and isAuthorized (camelCase)
          const isAuthorized = user?.is_authorized ?? user?.isAuthorized ?? false;
          console.log('Login response:', { user, is_authorized: user?.is_authorized, isAuthorized });
          set({
            user: user,
            isAuthenticated: true,
            isAuthorized: isAuthorized,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          console.error('Backend verification failed:', error);
          // Handle authorization errors specifically
          if (error.response?.status === 403) {
            set({
              user: null,
              isAuthenticated: false,
              isAuthorized: false,
              isLoading: false,
              error: error.response?.data?.detail || 'Access denied. Your account is not authorized to use this application.',
            });
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isAuthorized: false,
              isLoading: false,
              error: error.response?.data?.detail || 'Authentication failed',
            });
          }
        }
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isAuthorized: false,
          isLoading: false,
          error: null,
        });
      }
    });

    return unsubscribe;
  },

  signUp: async (email: string, password: string) => {
    set({ error: null });
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // Auth state change will be handled by onAuthStateChanged
    } catch (error: any) {
      let errorMessage = 'Sign up failed';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email already in use';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.message) {
        errorMessage = error.message;
      }
      set({ error: errorMessage });
      throw error;
    }
  },

  signIn: async (email: string, password: string) => {
    set({ error: null });
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state change will be handled by onAuthStateChanged
    } catch (error: any) {
      let errorMessage = 'Sign in failed';
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.message) {
        errorMessage = error.message;
      }
      set({ error: errorMessage, isAuthorized: false });
      throw error;
    }
  },

  signInWithGoogle: async () => {
    set({ error: null });
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // Auth state change will be handled by onAuthStateChanged
    } catch (error: any) {
      let errorMessage = 'Google sign in failed';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      set({ error: errorMessage, isAuthorized: false });
      throw error;
    }
  },

  signOut: async () => {
    try {
      await signOut(auth);
      await api.post('/auth/logout');
      set({
        user: null,
        isAuthenticated: false,
        isAuthorized: false,
        error: null,
      });
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  },
}));

